/**
 * 「这个字收录在哪几张表上」的**展示层**（`HanziReadingsModal` 用）。
 *
 * ## 为什么单独一个文件（用户实测到的 bug）
 *
 * 2026-10-17 用户实测：打开「人」的「读音与造词」弹框，里面是
 *
 *     收录在：
 *     收录情况没拿到（不是"没被收录"）。
 *
 * 而「人」明确就在下面那张识字表里。根因不是接口没数据，而是**弹框看错了地方**：
 *
 *   · 弹框读的是**点开时那张字卡**（`HanziView`）自带的 `listings`；
 *   · 「按书录入」tab 的字卡来自 `GET .../lists/items`（`listBookListings`），
 *     那个 DTO **不带** `listings`（它可选，见 `HanziDto.listings` 的注释）→
 *     `decodeHanzi` 得到 `null` → 界面按"拿不到"兜底；
 *   · 而**同一个字的 `/readings` 响应里 `hanzi.listings` 一直是有值的**
 *     （`getHanziReadings()` 里 `dto.listings = listings.map(toHanziListingDto)`），
 *     弹框已经拿到了这份 `data`，却只用了 `summary` / `wordsByReading`。
 *
 * 所以这里把"用哪一份数据"和"怎么变成一句人话"写在**一处**，三态（有收录 /
 * 真没收录 / 拿不到）各有一句固定的文案，组件只负责画：
 *
 * | 状态 | 触发 | 文案 |
 * | --- | --- | --- |
 * | `listed` | `listings` 是一组非空数组 | 逐行 `人民教育出版社 · 2024年8月第1版 · 小学一年级 · 上册 · 识字表` |
 * | `none` | `listings` 是空数组 | 「没有被收录到任何一张表」 |
 * | `unknown` | 两份数据都是 `null` | 「收录情况没拿到（不是"没被收录"）」 |
 * | `loading` | 同上，但请求还在飞 | 「正在查…」（别在半路上就说"拿不到"） |
 *
 * ⚠️ `none` 与 `unknown` **必须**分开说：前者是"这个字确实不在任何表上"（例如它
 *    只在词语表里出现过），后者是"这份数据没拿到"。把两者混在一起，界面就会
 *    一会儿说"没被收录"、一会儿说"没拿到"，用户无法据此判断该不该去录。
 */
import { gradeLabel, publisherDisplayNameOf } from '@fht/shared';
import type { HanziListingView, TextbookView } from '../../lib/api/dictation';
import { listTypeShortLabel, semesterLabel } from '../../lib/api/dictation';

/** 三态之外再加一个"还在查"——它不属于任何一态，不能拿它冒充 `unknown`。 */
export type HanziListingsState = 'loading' | 'listed' | 'none' | 'unknown';

/** `unknown` 那一句（**不是**"没被收录"）。 */
export const LISTINGS_UNKNOWN_TEXT = '收录情况没拿到（不是"没被收录"）—— 请刷新重试。';

/** `none` 那一句（真的没有任何收录）。 */
export const LISTINGS_NONE_TEXT =
  '没有被收录到任何一张表 —— 按表听写取不到它（只在词语表里出现过不算收录）。';

/** `loading` 那一句。 */
export const LISTINGS_LOADING_TEXT = '正在查这个字收录在哪几张表…';

/** 课本列表本身没拿到（`textbooks === null`）时，那一行课本名位置显示什么。 */
export const LISTINGS_TEXTBOOK_UNKNOWN = '（课本名没拿到）';

/** 课本列表拿到了，但里面没有这条收录指向的课本（数据不一致）时显示什么。 */
export const LISTINGS_TEXTBOOK_MISSING = '（这本课本不在课本列表里）';

/** 一条展示行：`id` 用 React key，`line` 是给人看的那句话。 */
export interface HanziListingLine {
  id: string;
  line: string;
}

export interface HanziListingsView {
  state: HanziListingsState;
  /** `state === 'listed'` 时才有内容；其余状态是空数组。 */
  entries: HanziListingLine[];
  /** `state !== 'listed'` 时的那句话；`listed` 时是空串。 */
  message: string;
}

/**
 * 取课本的展示名：`name` 优先，退回**出版社身份**（`publisherDisplayNameOf`：
 * `publisher` 是历史代号 `pub_xxxxxxxx` 时它会退回 `name`，所以代号不会露到界面上）。
 */
function textbookTitle(textbook: TextbookView): string {
  if (textbook.name !== '') return textbook.name;
  const publisher = publisherDisplayNameOf(textbook);
  return publisher !== '' ? publisher : LISTINGS_TEXTBOOK_UNKNOWN;
}

/**
 * 一条收录 → 一句人话：`人民教育出版社 · 小学一年级 · 上册 · 识字表`。
 *
 * ⚠️ 课本名 / 年级 / 学期**必须在**这里（用户原话："在哪一本书上出现在哪张表上，
 *    这很重要"）：一本书就是一册，只写"识字表"看不出是哪本书的字表。
 * ⛔ 不缀版次（2026-10-18 起彻底去掉）。
 */
export function describeListingLine(
  listing: HanziListingView,
  textbooks: readonly TextbookView[] | null,
): string {
  const textbook = textbooks?.find((item) => item.id === listing.textbookId) ?? null;
  const parts: string[] = [];
  if (textbook !== null) {
    /**
     * ⚠️ 只写**课本名**，年级 / 学期用 `listing` 自己那两个字段（见下）。
     *    一本书就是一册，两者本来就一致；分开写既不会重复，又能防住
     *    历史数据里"收录的册别与书不一致"那种情况照样显示得出来。
     */
    parts.push(textbookTitle(textbook));
  } else {
    parts.push(textbooks === null ? LISTINGS_TEXTBOOK_UNKNOWN : LISTINGS_TEXTBOOK_MISSING);
  }
  parts.push(
    gradeLabel(listing.gradeCode),
    semesterLabel(listing.semester),
    listTypeShortLabel(listing.listType),
  );
  /**
   * **这个位置标定的读音**（迁移 0018）：同一个字在同一张表上出现两次时，
   * 两行都写"识字表"就分不清哪个是哪个 —— 有标音就补一句。
   * ⚠️ `undefined`/空串（老数据、没标音）不补：280 个老字不该变成 280 句"未标音"。
   */
  if (typeof listing.reading === 'string' && listing.reading !== '') {
    parts.push(`读 ${listing.reading}`);
  }
  return parts.join(' · ');
}

/**
 * 两份数据源 → 一个三态视图。
 *
 * 优先级：**`/readings` 响应里的那一份** > 字卡自带的那一份。
 * 理由：`/readings` 是"专门为这个字查的"，返回该字的**全部**收录
 * （可能跨册、跨表）；字卡那份是从列表页带过来的，在「按书录入」tab 上
 * 根本没有（见文件头），在浏览 tab 上也只是个副产品。
 */
export function describeHanziListings(input: {
  /** `fetchHanziReadings()` 解出来的 `hanzi.listings`；`null` = 没拿到。 */
  fromReadings: HanziListingView[] | null;
  /** 点开弹框时那张字卡的 `listings`；`null` = 没拿到。 */
  fromCard: HanziListingView[] | null;
  /** 课本列表（用来把 `textbookId` 变成「人民教育出版社 · 2024年8月第1版」）。 */
  textbooks: readonly TextbookView[] | null;
  /** 读音请求还在飞？ */
  loading: boolean;
}): HanziListingsView {
  const listings = input.fromReadings ?? input.fromCard ?? null;
  if (listings === null) {
    return input.loading
      ? { state: 'loading', entries: [], message: LISTINGS_LOADING_TEXT }
      : { state: 'unknown', entries: [], message: LISTINGS_UNKNOWN_TEXT };
  }
  if (listings.length === 0) {
    return { state: 'none', entries: [], message: LISTINGS_NONE_TEXT };
  }
  return {
    state: 'listed',
    entries: listings.map((listing) => ({
      id: listing.id,
      line: describeListingLine(listing, input.textbooks),
    })),
    message: '',
  };
}
